import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Map, Target, ChevronRight, ChevronLeft } from 'lucide-react';
const STEPS = [{
  id: 1,
  title: 'Define Your Market Lens',
  description: "Define your ideal customers and uncover where to grow next. Use your data + Windfall intelligence to find where you're winning, where you're under-penetrated, and where the biggest opportunities exist.",
  icon: Search,
  color: 'text-teal-600',
  bgColor: 'bg-teal-50'
}, {
  id: 2,
  title: 'Discover TAM & Identify Opportunity',
  description: 'Visualize your current market share, understand market penetration, and discover untapped whitespace opportunities across different regions.',
  icon: Map,
  color: 'text-blue-500',
  bgColor: 'bg-blue-50'
}, {
  id: 3,
  title: 'Create Segment & Take Action',
  description: 'Get actionable, AI-powered recommendations on where to focus your expansion efforts next and create targeted segments to drive growth.',
  icon: Target,
  color: 'text-indigo-600',
  bgColor: 'bg-indigo-50'
}];
export function OnboardingFlow({
  onComplete
}: {
  onComplete: () => void;
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setDirection(1);
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete();
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleDotClick = (index: number) => {
    setDirection(index > currentStep ? 1 : -1);
    setCurrentStep(index);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };
  const step = STEPS[currentStep];
  const Icon = step.icon;
  return <div className="flex-1 flex flex-col bg-white relative overflow-hidden h-full" data-id="element-646">
      <div className="absolute top-6 right-6 z-20" data-id="element-647">
        <button onClick={onComplete} className="text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors" data-id="element-648">
          Skip
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center relative px-6 w-full" data-id="element-649">
        <AnimatePresence mode="wait" custom={direction} data-id="element-650">
          <motion.div key={currentStep} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.2
          }
        }} className="flex flex-col items-center text-center max-w-xl w-full" data-id="element-651">
            <div className={`w-40 h-40 rounded-full flex items-center justify-center mb-10 ${step.bgColor}`} data-id="element-652">
              <Icon size={64} className={step.color} data-id="element-653" />
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-654">
              {step.title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-655">
              {step.description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="p-8 flex flex-col items-center z-10 bg-white" data-id="element-656">
        <div className="flex space-x-3 mb-8" data-id="element-657">
          {STEPS.map((_, idx) => <button key={idx} onClick={() => handleDotClick(idx)} className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentStep ? 'bg-teal-600' : 'bg-gray-200 hover:bg-gray-300'}`} aria-label={`Go to step ${idx + 1}`} data-id="element-658" />)}
        </div>

        <div className="flex items-center justify-between w-full max-w-md" data-id="element-659">
          <button onClick={handleBack} className={`px-6 py-3 text-sm font-medium rounded-xl transition-colors flex items-center ${currentStep === 0 ? 'invisible' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-660">
            <ChevronLeft size={18} className="mr-1" data-id="element-661" /> Back
          </button>

          <button onClick={handleNext} className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm flex items-center" data-id="element-662">
            {currentStep === STEPS.length - 1 ? 'Get Started' : 'Next'}
            {currentStep !== STEPS.length - 1 && <ChevronRight size={18} className="ml-1" data-id="element-663" />}
          </button>
        </div>
      </div>
    </div>;
}